<template>
	<td :data-grid-id="gridId" class="grid-cell indicator frozen fixed" data-id="rowIndicator" style="" data-distance="0">
		<div class="grid-cell-content">
			<div class="icon">
				<icon v-if="item.$___locked" icon="lock-alt" />
				<icon v-else-if="item.rowState === 'new'" icon="circle" />
				<icon v-else-if="item.$___dirtyCells.length > 0" icon="edit" />
				<icon v-else-if="item.rowState === 'deleted'" icon="trash" />
				<icon v-else-if="!item.$___valid" icon="exclamation-circle" />
			</div>
		</div>
	</td>

	<td :data-grid-id="gridId" v-if="settings.checkboxesEnabled" class="grid-cell checkbox frozen fixed" data-id="rowCheckbox" @click="(e) => $emit('row_checkboxClicked', { e, item, index })" style="" data-distance="0">
		<div class="grid-cell-content">
			<icon :icon="item.$___checked ? 'check-square' : 'square'" />
		</div>
	</td>

	<td :data-grid-id="gridId" v-for="col of filteredColumns" :key="col.id" :set="colEditable = col_isEditable(col)" :data-id="col.id" :class="{
		'grid-cell': true,
		frozen: col.pinned,
		fixed: !col.draggable,
		'reverse-dir': col.reverseDir,
		'cell-active': col.isActive,
		'grid-cell-changed': col_isInDirtyList(item, col.name),
		'grid-cell-error':
			item.$___invalidCells &&
			item.$___invalidCells.indexOf(col.id) > -1,
		[getStyleRule(item, col)]: true
	}" :style="'order: ' +
		col.order +
		';' +
		(!col.visibility ? 'display: none' : 'display: table-cell')
		" @click.exact="$emit('focus_cellClicked', { index, item, col })" @click.ctrl="$emit('focus_cellClickedByCtrl', item)" :tabindex="index !== activeRowIndex ||
									!col.isActive ||
									!colEditable
									? 0
									: null
									" :data-distance="col.$___distance > -1 ? col.$___distance : null">
		<div v-if="col_isRowNumber(col)" class="grid-cell-content" :data-width="col.width">
			<div class="grid-cell-content-view" :tabindex="col.isActive ? 0 : ''" :class="{ 'text-center': col.align == 'center', 'text-left': col.align == 'left' }">
				{{ String(index + 1).toLocaleDigits() }}
			</div>
		</div>

		<div v-else-if="index === activeRowIndex && col.isActive && colEditable" class="grid-cell-content" :style="[col.width == 'auto' ? { 'white-space': 'pre' } : {}]" :data-width="col.width" :set="edit = col_edit(item, col)">
			<component :is="makeRawComponent(edit.component)" :modelValue="col_getProp(item, col)" @update:modelValue="(val) => col_setProp(item, col, val)" v-on:focusout="$emit('cell_focused_out')" :options="edit.options" :tabindex="col.isActive ? 0 : ''" class="grid-cell-content-editor"></component>
		</div>

		<div v-else class="grid-cell-content" :data-width="col.width" :style="[col.width == 'auto' ? { 'white-space': 'pre' } : {}]">
			<div v-if="index !== activeRowIndex || !col.isActive || !colEditable" class="label" :class="{ 'text-center': col.align == 'center', 'text-left': col.align == 'left', 'ltr': col.direction == 'ltr' }" :set="view = col_view(item, col)">
				<div v-if="!view" class="grid-cell-content-view" :title="item[col.name]" :tabindex="0">{{ item[col.name] }}</div>
				<div v-else-if="!view.component" class="grid-cell-content-view" :title="view.value" :tabindex="0" v-html="view.value"></div>
				<component v-else :is="makeRawComponent(view.component)" :modelValue="col_getProp(item, col)" :options="view.options" @update:modelValue="(val) => col_setProp(item, col, val)" @click="componentClick" class="grid-cell-content-view" :class="col.cellClass" :tabindex="0"></component>
			</div>
		</div>
	</td>
</template>
<script>
	import _ from "lodash"
	import { ref, nextTick } from "vue"
	import { toRaw, isReactive } from "vue"
	import textVisualizer from "@nikan/text-visualizer"

	const SYSFIELDS_PREFIX = "$___"
	const SYSFIELDS_DIRTYCELLS = SYSFIELDS_PREFIX + "dirtyCells"
	const SYSFIELDS_LOCKED = SYSFIELDS_PREFIX + 'locked'

	export default {
		name: 'cells',
		props: {
			gridId: String,
			item: Object,
      filteredData: Array,
			index: Number,
			activeRowIndex: Number,
			settings: Object,
			filteredColumns: Array,
			isRowDisabled: Boolean
		},
		mounted() {
			this.$emit('cell_mounted')
		},
		emits: [
			"data-changed",
			"focus_cellClicked",
			"focus_cellClickedByCtrl",
			"row_checkboxClicked",
			"cell_mounted",
			"cell_focused_out",
		],
		data() {
			return {
			}
		},
		methods: {
			col_isEditable(col) {
				if (
					this.settings.editable === false ||
					col.editable == null ||
					this.isRowDisabled ||
					this.item[SYSFIELDS_LOCKED] === true
				) {
					return false
				}

				return _.isFunction(col.editable) ? col.editable(this.item, col) : !!col.editable
			},
			col_isInDirtyList(row, colId) {
				return row[SYSFIELDS_DIRTYCELLS]?.indexOf(colId) > -1
			},

			col_isRowNumber(col) {
				return col.name && col.name.toLowerCase() === "rownumber"
			},
			col_getProp(row, col) {
				return _.get(row, col.name)
			},

			col_setProp(row, col, val) {
				_.set(row, col.name, val)
				this.$emit("data-changed", { column: col.name, val: val, index: this.index, item: row, col })
			},
			col_validateColConfig(col, config) {
				if (!config.component) {
					console.warn(`table column '${col.name}' has not provided any component.`)
				}

				return config
			},
			col_edit(row, col) {
				col.component = this.makeRawComponent(col.component)
				return this.col_validateColConfig(
					col,
					_.isFunction(col.edit) ? col.edit(row, col, this.filteredData) : col.edit || {}
				)
			},
			col_view(row, col) {
				col.component = this.makeRawComponent(col.component)
				let config = _.isFunction(col.view) ? col.view(row, col) : col.view
				if (!config) return null

				if (!config.component) {
					if (!row[col.name] && row[col.name] !== 0) return { value: "" }

					var value = row[col.name]

					if (config.options) {
						if (config.options.format && _.isFunction(config.options.format))
							return { value: config.options.format(value) }

						if (config.options.type)
							return { value: textVisualizer(value, config.options) }
					}
				}

				return config
			},
			getStyleRule(row, col) {
				return this.settings.cellStyleRule ? this.settings.cellStyleRule(row, col) : ''
			},
			componentClick(button, e) {
				if (button && button.handle)
					button.handle()
			}
		},
	}
</script>
